import styled from 'styled-components';

import { StyleColor, NewStyleColor } from '../../../utils/styles-constants';
import { hexToRgba, spin } from '../../../utils/style';
import { EditorType } from '../../../containers/Editor/constants';
import UserAgentUtils from '../../../utils/userAgent';

interface PageWrapperProps {
	editorType: EditorType;
	zoom: number;
}

interface StyledPageProps {
	width: number;
	height: number;
	isTransparent: boolean;
	isSomeEditableItemActive: boolean;
}

interface PageInnerProps {
	scale: number;
}

interface StyledIframeProps {
	isLoading: boolean;
}

const PageWrapper = styled('div')`
	flex: 1 1 auto;

	${({ editorType, zoom }: PageWrapperProps) => editorType === EditorType.INDESIGN && zoom <= 1 && `
		overflow: hidden;
	`}

	${({ editorType }: PageWrapperProps) => editorType === EditorType.HTML && `
		position: relative;
		display: flex;
		flex-direction: column;
	`}

	${({ editorType, zoom }: PageWrapperProps) => editorType === EditorType.HTML && zoom !== 1 && `
		transform: scale(${zoom});
		transform-origin: ${zoom < 1 ? 'center top' : '0 0'};
	`}
`;

const StyledPage = styled('div')`
	width: ${({ width }: StyledPageProps) => `${width}px`};
	height: ${({ height }: StyledPageProps) => `${height}px`};
	margin: 0 auto;
	background-color: ${({ isTransparent }: StyledPageProps) => isTransparent ? 'transparent' : StyleColor.White};

	${({ isSomeEditableItemActive }: StyledPageProps) => !isSomeEditableItemActive && `overflow: hidden;`}

	/* this style is needed for proper line-height handling */
	[class*="editable-text"] p span {
		vertical-align: bottom;
	}

	[class*="editable-text"] {
		cursor: text;

		&.edited {
			overflow: visible;
		}
	}

	.is-visible {
		overflow: visible !important;

		> * {
			overflow: visible !important;
		}
	}

	[class*="editable-image"] {
		overflow: visible !important;

		&.is-active {
			z-index: 2;
		}

		img {
			min-width: auto !important;
		}
	}

	/* we have to disable pointer-events on all elements for selecting editable text fields and images
	(for example, when there are buttons, links or layers above text fields and images) */
	> * {
		pointer-events: none;
	}

	._idGenButton:not(.editor-btn-trigger) {
		cursor: default;
	}

	.editor-btn-trigger {
		position: absolute;
		bottom: 0;
		right: 0;
		z-index: 2;
		width: 25px;
		height: 25px;
		border-radius: 50%;
		border: 0;
		outline: 0;
		background: url('./.gallery/static/images/edit-button.svg');
		background-size: 100%;
		pointer-events: auto;
		cursor: pointer;

		&:hover {
			background: url('./.gallery/static/images/edit-button-inverse.svg');
			background-size: 100%;
		}
	}

	.editable-button .edited[class*="editable-text"],
	.editable-button .edited[class*="editable-text"] * {
		pointer-events: auto;
	}

	[class*="locked"] {
		background-color: ${NewStyleColor.Grey300};
	}
`;

const PageInner = styled('div')`
	transform: scale(${({ scale }: PageInnerProps) => scale});
	transform-origin: 0 0;
`;

const StyledIframe = styled('iframe')`
	flex: 1 1 auto;
	box-sizing: border-box;
	width: 100%;
	height: 100%;
	max-width: 100%;
	max-height: 100%;
	padding: 27px;
	border: 0;
	overflow: hidden;
	transition: opacity ease 1s;

	${({ isLoading }: StyledIframeProps) => isLoading && 'opacity: 0;'}
`;

const Loader = styled('div')`
	position: absolute;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: bold;
	font-size: 21px;
	color: ${NewStyleColor.White};

	svg {
		height: 35px;
		width: 35px;
		animation: ${spin};
		margin-right: 20px;
	}
`;

// these styles are injected in iframe for styling editable components (RichTextEditor, MediaEditor, LinkEditor)
const htmlEditorStyles = `
	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.uprez-rte-wrapper {
		position: relative;
		overflow: hidden;
		pointer-events: auto;
	}

	.uprez-rte-wrapper.is-active {
		outline: 2px solid ${NewStyleColor.Blue100};
	}

	.uprez-rte-wrapper > div {
		height: 100%;
	}

	.uprez-rte-wrapper a {
		color: inherit;
		text-decoration: none;
	}

	.uprez-rte {
		overflow-y: auto;
		overflow-x: hidden;
		padding-right: 6px;
		scrollbar-width: thin;
		scrollbar-color: ${NewStyleColor.Grey500} ${NewStyleColor.Grey100};
	}

	.uprez-rte::-webkit-scrollbar {
		width: 5px;
		background-color: ${NewStyleColor.Grey100};
	}

	.uprez-rte::-webkit-scrollbar-track {
		background-color: ${NewStyleColor.Grey100};
		border-radius: 3px;
	}

	.uprez-rte::-webkit-scrollbar-thumb {
		background-color: ${NewStyleColor.Grey500};
		border-radius: 3px;
	}

	[data-uprez-image],
	[data-uprez-video] {
		position: relative;
		display: inline-block; /* to keep centered image/video in case parent element has text-align: center; property */
		vertical-align: middle;
	}

	[data-uprez-image] > img,
	[data-uprez-video] > img {
		width: 100%;
		vertical-align: bottom;
	}

	.uprez-media-frame {
		position: relative;
		overflow: hidden;
		pointer-events: auto;
		border-radius: inherit;
	}

	.uprez-media-frame.is-active {
		z-index: 100; /* to overlap other elements on the page when drag or resize image */
		overflow: visible;
	}

	.uprez-media-frame.is-active:not(.is-video-cover) img {
		width: 100%;
	}

	.uprez-media-frame.is-active.can-drag {
		pointer-events: none;
	}

	[class*="is-rounded"] .uprez-media-frame {
		border-radius: 50%;
	}

	.is-video-cover img,
	[data-uprez-image] svg,
	[data-uprez-video] svg {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		max-width: 100%;
		max-height: 100%;
		width: auto;
		height: auto;
		margin: auto;
		pointer-events: auto;
	}

	.is-default-video {
		overflow: hidden;
	}

	.is-default-video img {
		width: 100%;
		max-height: none;
		margin: 0;
	}

	[data-uprez-image] svg,
	[data-uprez-video] svg {
		width: 50%;
		height: 50%;
		fill: ${NewStyleColor.Grey400};
	}

	.uprez-media-frame-border {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		border: 2px solid ${NewStyleColor.Grey400};
		border-radius: inherit;
	}

	.uprez-media-frame-border.is-active {
		border-color: ${NewStyleColor.Blue100};
	}

	.uprez-media-rnd {
		pointer-events: auto;
	}

	.uprez-media-rnd.is-active {
		z-index: -1;
	}

	.uprez-media-rnd.is-active img {
		outline: 2px solid ${NewStyleColor.Blue100};
	}

	.uprez-media-rnd.is-active .editor-media-controls div::before,
	.uprez-media-rnd.is-active .editor-media-controls div::after {
		content: '';
		position: absolute;
		top: 50%;
		left: 50%;
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
	}

	.uprez-media-rnd.is-active .editor-media-controls div::before {
		width: 400%;
		height: 400%;
	}

	.uprez-media-rnd.is-active .editor-media-controls div::after {
		width: 100%;
		height: 100%;
		outline: 1px solid ${NewStyleColor.Blue100};
		background-color: ${NewStyleColor.White};
	}

	.uprez-media-frame-loader {
		position: absolute;
		top: 2px;
		bottom: 2px;
		left: 2px;
		right: 2px;
		z-index: 1;
	}

	.uprez-media-frame-mask {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		background-color: ${NewStyleColor.White};
		opacity: 0.9;
	}

	.uprez-media-frame-loader-inner {
		position: absolute;
		top: 50%;
		left: 50%;
		-webkit-transform: translate(-50%, -50%);
		transform: translate(-50%, -50%);
		padding: 5px;
		font-family: HelveticaNeue, sans-serif;
		font-size: 16px;
		line-height: 20px;
		color: ${NewStyleColor.Grey800};
		text-align: center;
	}

	.uprez-media-frame-loader-inner span {
		display: block;
	}

	.uprez-media-frame-spinner {
		position: relative;
		display: inline-block;
		width: 45px;
		height: 45px;
		color: ${StyleColor.Blue600};
		border-radius: 50%;
		animation: spin 2s linear infinite;
	}

	.uprez-media-frame-spinner::before,
	.uprez-media-frame-spinner::after {
		content: '';
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		border-radius: 50%;
	}

	.uprez-media-frame-spinner::before {
		border: 4px solid currentColor;
		opacity: 0.2;
	}

	.uprez-media-frame-spinner::after {
		border: 4px solid currentColor;
		border-top-color: transparent;
	}

	.uprez-link-editor {
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 1;
		color: ${NewStyleColor.Grey400};
		border: 2px dashed currentColor;
		pointer-events: auto;
	}

	.uprez-link-editor.is-active {
		color: ${NewStyleColor.Blue100};
	}

	.uprez-link-editor.has-image {
		pointer-events: none;
	}

	.uprez-link-editor-icon {
		position: absolute;
		top: 0;
		right: 0;
		display: -webkit-box;
		display: -ms-flexbox;
		display: flex;
		-webkit-box-pack: center;
		-ms-flex-pack: center;
		justify-content: center;
		-webkit-box-align: center;
		-ms-flex-align: center;
		align-items: center;
		width: 20px;
		height: 20px;
		border-radius: 50%;
		background-color: currentColor;
	}

	.uprez-link-editor-icon svg {
		width: 14px;
		height: 14px;
		fill: ${NewStyleColor.White};
	}

	.rte-underline .rte-font-color {
		text-decoration: underline !important;
	}

	body:not(.no-user-select) * {
		user-select: auto !important;
	}

	body.no-user-select {
		user-select: none;
	}

	html {
		background: ${NewStyleColor.White};
	}

	${!UserAgentUtils.isMobileApp
		? `
			body::-webkit-scrollbar {
				width: 16px;
			}

			body::-webkit-scrollbar-track,
			body::-webkit-scrollbar-corner {
				background-color: ${NewStyleColor.Grey950};
			}

			body::-webkit-scrollbar-thumb {
				background-color: ${hexToRgba(NewStyleColor.White, 20)};
			}
		`
		: ''
	}
`;

export {
	PageWrapper,
	StyledPage,
	PageInner,
	StyledIframe,
	Loader,
	htmlEditorStyles,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/Page/styles.ts